/**
 * "Only when" — the rule's guards INSIDE its conditions (user, 20/10/2026:
 * "it has to be inside the conditions … how many times it fails"): the
 * minimum sample, the settle time and "it happened on ≥ N drops" (each drop
 * judged on its own numbers) are rows under the conditions, each with a ✕;
 * a missing one comes back from its "+" pill. 20/10/2026 — all optional
 * (user: "make them optional by default please!! everywhere"): every card
 * starts with none, and a guard the card shares with the list rule has its
 * ✕ too (it says so — removing it here removes it there).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Hourglass, Repeat, Users } from 'lucide-react';
import type { DropGenTimesMode } from '@trafficpulse/shared';

import { SegmentedPills } from '@/components/ui';

import { HINTS, type OnlyWhenScope, sampleHint, sampleUnit, settleUnit, START, TIMES_MODES, TIMES_NOTE, timesUnit } from './only-when-words';
import { OnlyWhenRow } from './OnlyWhenRow';

interface Guard {
  value: number;
  onChange: (v: number) => void;
  /** The list rule's own setting, edited (and removed) from this card too. */
  shared?: boolean;
}

export interface OnlyWhenProps {
  /** The minimum sample: settled SMS on drops, unique visits on a tracker source. */
  sample: Guard & { unit: 'sms' | 'visits' };
  /** The settle time. Absent = no settle on this source. */
  settle?: Guard;
  /** "It happened on ≥ N drops" in total or in a row — absent where the card judges one total or one drop. */
  times?: Omit<Guard, 'shared'> & { mode: DropGenTimesMode; onMode: (mode: DropGenTimesMode) => void };
  /** What one judgement counts (default: the day's drops). */
  scope?: OnlyWhenScope;
}

type AddPill = { key: string; label: string; on: () => void };

/** The "+" pills for the rows that are off. */
const missing = ({ sample, settle, times }: OnlyWhenProps): AddPill[] =>
  [
    sample.value <= 0 ? { key: 'sample', label: 'Minimum sample', on: () => sample.onChange(START.sample) } : null,
    settle && settle.value <= 0 ? { key: 'settle', label: 'Settle time', on: () => settle.onChange(START.settle) } : null,
    times && times.value <= 1 ? { key: 'times', label: 'Happened on N drops', on: () => times.onChange(START.times) } : null,
  ].filter((a): a is AddPill => a !== null);

const Rows: FC<OnlyWhenProps> = ({ sample, settle, times, scope = 'day' }) => {
  const visits = sample.unit === 'visits';
  const shared = { sample: sample.shared === true, settle: settle?.shared === true };
  return (
    <>
      {sample.value > 0 ? (
        <OnlyWhenRow testId="only-when-sample" icon={Users} label={visits ? 'Unique visits' : 'Settled SMS'} atLeast value={sample.value} onChange={(v) => sample.onChange(Math.max(1, v))} min={1} unit={sampleUnit(scope, visits, shared.sample)} hint={sampleHint(scope, visits, shared.sample)} onRemove={() => sample.onChange(0)} />
      ) : null}
      {settle && settle.value > 0 ? (
        <OnlyWhenRow testId="only-when-settle" icon={Hourglass} label="A drop counts" value={settle.value} onChange={(v) => settle.onChange(Math.max(1, v))} min={1} max={1440} unit={settleUnit(shared.settle)} hint={shared.settle ? HINTS.shared : HINTS.settle} onRemove={() => settle.onChange(0)} />
      ) : null}
      {times && times.value > 1 ? (
        <OnlyWhenRow testId="only-when-times" icon={Repeat} label="It happened on" atLeast value={times.value} onChange={(v) => times.onChange(Math.max(2, v))} min={2} max={50} unit={timesUnit(scope, times.value)} extra={<TimesMode mode={times.mode} onMode={times.onMode} />} hint={HINTS.times} onRemove={() => times.onChange(1)} />
      ) : null}
    </>
  );
};

/** In total / in a row, with what the picked one means. */
const TimesMode: FC<{ mode: DropGenTimesMode; onMode: (mode: DropGenTimesMode) => void }> = ({ mode, onMode }) => (
  <span className="flex items-center gap-1.5" data-testid="only-when-times-mode">
    <SegmentedPills options={TIMES_MODES} value={mode} onChange={onMode} />
    <span className="text-[11px] text-muted-foreground">{TIMES_NOTE[mode]}</span>
  </span>
);

const Add: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button type="button" onClick={onClick} className="inline-flex h-7 items-center rounded-md border border-dashed border-border px-2.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary">
    + {label}
  </button>
);

export const OnlyWhen: FC<OnlyWhenProps> = (props) => {
  const adds = missing(props);
  return (
    <div className="space-y-2 pt-1" data-testid="only-when">
      <div className="flex items-center gap-2 py-0.5">
        <span className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">only when</span>
        <span className="h-px flex-1 bg-border/50" />
      </div>
      <Rows {...props} />
      {adds.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {adds.map((a) => (
            <Add key={a.key} label={a.label} onClick={a.on} />
          ))}
        </div>
      ) : null}
    </div>
  );
};
